<template>
  <subscriptions-home v-if="showAgentsOverview" />
  <main v-else class="agents-dashboard-page">
    <div class="agents-dashboard-content">
      <div class="dashboard-header">
        <div class="brand-icon">
          <v-icon color="#1876d2">mdi-creation-outline</v-icon>
        </div>
        <div>
          <h1>Fresher AI</h1>
          <p>Administra tus agentes y créditos desde un solo lugar.</p>
        </div>
      </div>

      <v-alert
        v-if="error"
        type="error"
        outlined
        class="dashboard-alert"
      >
        {{ error }}
      </v-alert>

      <div v-if="loading" class="dashboard-loading">
        <v-skeleton-loader type="card" />
        <div class="dashboard-grid">
          <v-skeleton-loader type="card" />
          <v-skeleton-loader type="card" />
        </div>
      </div>

      <div v-else>
        <section class="dashboard-section">
          <v-card class="dashboard-card credits-card" flat>
            <div class="credits-card-top">
              <div>
                <h2>Créditos disponibles</h2>
                <p class="plan-line">
                  {{ credits.planName }} • Renovación: {{ credits.renewalDate }}
                </p>
              </div>

              <v-btn
                depressed
                class="buy-credits-button"
                @click="handleBuyCredits"
              >
                Comprar créditos
              </v-btn>
            </div>

            <div class="credits-balance">
              <strong>{{ formatNumber(credits.available) }}</strong>
              <span>créditos disponibles</span>
            </div>

            <div class="credits-breakdown">
              <div>
                <span>Créditos mensuales</span>
                <strong>{{ formatNumber(credits.monthlyAvailable) }}</strong>
              </div>
              <div>
                <span>Créditos comprados</span>
                <strong>{{ formatNumber(credits.purchasedAvailable) }}</strong>
              </div>
            </div>
          </v-card>
        </section>

        <section class="dashboard-grid">
          <v-card class="dashboard-card risk-agent-card" flat>
            <div class="risk-agent-heading">
              <div class="risk-agent-icon">
                <v-icon color="#1876d2">mdi-shield-search-outline</v-icon>
              </div>
              <div>
                <span class="eyebrow">AGENTE DISPONIBLE</span>
                <h2>Agente de Riesgo</h2>
                <p>
                  Consulta empresas y personas para apoyar tus procesos de validación y cumplimiento.
                </p>
              </div>
            </div>

            <v-btn depressed class="primary-action" @click="handleOpenRiskAgent">
              Abrir agente
              <v-icon small right>mdi-arrow-right</v-icon>
            </v-btn>
          </v-card>

          <v-card class="dashboard-card activity-card" flat>
            <h2>Actividad reciente</h2>

            <div v-if="!recentActivity.length" class="activity-empty">
              <div class="activity-empty-icon">
                <v-icon color="#7a8699">mdi-history</v-icon>
              </div>
              <div>
                <h3>Aún no hay actividad</h3>
                <p>Tus movimientos de créditos y pagos aparecerán aquí.</p>
              </div>
            </div>

            <div v-else class="activity-list">
              <div
                v-for="(activity, index) in recentActivity"
                :key="activity.id"
                class="activity-row"
                :class="{ 'with-border': index < recentActivity.length - 1 }"
              >
                <div class="activity-icon" :class="activity.iconClass">
                  <v-icon :color="activity.color">{{ activity.icon }}</v-icon>
                </div>

                <div class="activity-content">
                  <div class="activity-title-line">
                    <h3>{{ activity.title }}</h3>
                    <v-chip small label color="#f1f4f7" text-color="#18202d">
                      {{ activity.credits }}
                    </v-chip>
                  </div>
                  <p>{{ activity.description }}</p>
                  <div v-if="activity.timeAgo || activity.status" class="activity-meta">
                    <span v-if="activity.timeAgo">
                      <v-icon x-small color="#7a8699">mdi-clock-outline</v-icon>
                      {{ activity.timeAgo }}
                    </span>
                    <v-chip
                      v-if="activity.status"
                      x-small
                      label
                      color="#eef1f5"
                      text-color="#465266"
                    >
                      {{ activity.status }}
                    </v-chip>
                  </div>
                </div>
              </div>
            </div>
          </v-card>
        </section>
      </div>
    </div>
  </main>
</template>

<script>
import SubscriptionsHome from './Home.vue';

export default {
  name: 'Dashboard',
  components: { SubscriptionsHome },
  data() {
    return {
      loading: true,
      error: null,
      showAgentsOverview: false,
      credits: {
        planName: 'Sin plan activo',
        renewalDate: 'N/A',
        available: 0,
        monthlyAvailable: 0,
        purchasedAvailable: 0,
      },
      recentActivity: [],
    };
  },
  methods: {
    getLanguage() {
      return this.$store?.state?.language || localStorage.getItem('lang') || 'en';
    },
    getAuthConfig() {
      return {
        headers: {
          Authorization: this.$store.state.sToken
            ? `Bearer ${this.$store.state.sToken}`
            : '',
        },
      };
    },
    apiUrl(path) {
      return `${URI}/api/v1/${this.getLanguage()}/billing${path}`;
    },
    priceIntelligenceApiUrl(path) {
      return `${URI}/api/v1/${this.getLanguage()}/price-intelligence${path}`;
    },
    formatNumber(value) {
      return Number(value).toLocaleString('en-US');
    },
    formatDate(value) {
      if (!value) return 'N/A';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },
    timeAgo(value) {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const diffMs = Date.now() - date.getTime();
      const diffMin = Math.max(1, Math.floor(diffMs / 60000));
      if (diffMin < 60) return `Hace ${diffMin} minuto${diffMin === 1 ? '' : 's'}`;
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `Hace ${diffHr} hora${diffHr === 1 ? '' : 's'}`;
      const diffDay = Math.floor(diffHr / 24);
      return `Hace ${diffDay} día${diffDay === 1 ? '' : 's'}`;
    },
    mapTransactionStatus(status) {
      const normalized = String(status || '').toLowerCase();
      if (normalized === 'paid') return 'Pagado';
      if (normalized === 'pending') return 'Pendiente';
      if (normalized === 'failed') return 'Fallido';
      if (normalized === 'refunded') return 'Reembolsado';
      return status || '';
    },
    transactionActivityIcon(txType) {
      const normalized = String(txType || '').toLowerCase();
      if (normalized === 'subscription') {
        return { icon: 'mdi-receipt-text-outline', color: '#2163FF', iconClass: 'activity-icon-blue' };
      }
      if (normalized === 'credit_purchase') {
        return { icon: 'mdi-creation-outline', color: '#EC0B8C', iconClass: 'activity-icon-pink' };
      }
      return { icon: 'mdi-cash', color: '#00A342', iconClass: 'activity-icon-green' };
    },
    movementActivityIcon(movementType) {
      const normalized = String(movementType || '').toLowerCase();
      if (normalized === 'consume') {
        return { icon: 'mdi-trending-down', color: '#FF5B00', iconClass: 'activity-icon-orange' };
      }
      return { icon: 'mdi-trending-up', color: '#00A342', iconClass: 'activity-icon-green' };
    },
    buildActivities(transactions, movements) {
      const txActivities = (transactions || []).map((tx) => {
        const amount = Number(tx.amountCents || 0) / 100;
        const style = this.transactionActivityIcon(tx.transactionType);
        return {
          id: `tx-${tx.id}`,
          title: tx.description || 'Transacción',
          description: `Tipo: ${tx.transactionType || 'billing'}`,
          timeAgo: this.timeAgo(tx.paidAt),
          status: this.mapTransactionStatus(tx.status),
          credits: `${amount > 0 ? '+' : ''}${this.formatNumber(Math.abs(amount))} USD`,
          icon: style.icon,
          color: style.color,
          iconClass: style.iconClass,
          createdAt: tx.paidAt || null,
        };
      });

      const movementActivities = (movements || []).map((mv) => {
        const amount = Number(mv.amount || 0);
        const style = this.movementActivityIcon(mv.movementType);
        return {
          id: `mv-${mv.id}`,
          title: mv.movementType === 'consume' ? 'Consumo de créditos' : 'Abono de créditos',
          description: `Tipo: ${mv.creditType || 'general'}`,
          timeAgo: this.timeAgo(mv.createdAt),
          status: '',
          credits: `${amount > 0 ? '+' : ''}${this.formatNumber(amount)} créditos`,
          icon: style.icon,
          color: style.color,
          iconClass: style.iconClass,
          createdAt: mv.createdAt || null,
        };
      });

      return [...txActivities, ...movementActivities]
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 8)
        .map(({ createdAt, ...activity }) => activity);
    },
    async loadBillingDashboard() {
      this.loading = true;
      this.error = null;
      this.showAgentsOverview = false;
      try {
        const config = this.getAuthConfig();
        const [creditsRes, statusRes] = await Promise.all([
          DB.get(this.apiUrl('/credits'), config),
          DB.get(this.apiUrl('/status'), config),
        ]);
        const [transactionsResult, movementsResult] = await Promise.allSettled([
          DB.get(this.apiUrl('/transactions?limit=20'), config),
          DB.get(this.apiUrl('/credit-movements?limit=20'), config),
        ]);

        const sourceBreakdown = creditsRes?.data?.breakdown || {};
        const monthlyAvailable = Number(sourceBreakdown.subscription || 0);
        const purchasedAvailable = Number(sourceBreakdown.purchase || 0);
        const available = monthlyAvailable + purchasedAvailable;

        const subscription = statusRes?.data?.subscription || null;
        let planName = 'Sin plan activo';
        if (subscription?.planId) {
          const plansRes = await DB.get(this.apiUrl('/plans'), config);
          const matchedPlan = (plansRes?.data?.plans || []).find(
            (plan) => String(plan.id) === String(subscription.planId),
          );
          planName = matchedPlan?.name || `Plan ${subscription.planId}`;
        }

        this.credits = {
          planName,
          renewalDate: this.formatDate(subscription?.currentPeriodEnd),
          available,
          monthlyAvailable,
          purchasedAvailable,
        };

        this.recentActivity = this.buildActivities(
          transactionsResult.status === 'fulfilled'
            ? transactionsResult.value?.data?.transactions || []
            : [],
          movementsResult.status === 'fulfilled'
            ? movementsResult.value?.data?.movements || []
            : [],
        );

        // Only show the sales overview after billing has confirmed both values.
        // Missing data must not be interpreted as an empty balance.
        const creditRows = creditsRes?.data?.credits;
        const hasBreakdown = creditsRes?.data?.breakdown
          && typeof creditsRes.data.breakdown === 'object';
        const balance = Array.isArray(creditRows)
          ? creditRows.reduce((sum, row) => sum + Number(row.available), 0)
          : hasBreakdown ? available : NaN;
        const subscriptionKnown = statusRes?.data
          && Object.prototype.hasOwnProperty.call(statusRes.data, 'subscription');
        const subscriptionStatus = String(subscription?.status || '').toLowerCase();
        const hasActivePlan = ['active', 'trialing'].includes(subscriptionStatus);
        this.showAgentsOverview = Boolean(subscriptionKnown)
          && Number.isFinite(balance)
          && !hasActivePlan
          && balance <= 0;
      } catch (err) {
        this.error = err?.response?.data?.message || err.message || 'No se pudo cargar dashboard billing';
      } finally {
        this.loading = false;
      }
    },
    handleBuyCredits() {
      this.$router.push('/bills').catch(() => {});
    },
    handleOpenRiskAgent() {
      this.$router.push('/dueDiligence').catch(() => {});
    },
  },
  mounted() {
    this.loadBillingDashboard();
    this.loadPriceIntelligenceSummary();
  },
};
</script>

<style scoped>
.agents-dashboard-page {
  min-height: 100vh;
  background: #f7f8fa;
  color: #0c0d10;
}

.dashboard-header {
  display: flex;
  align-items: center;
  gap: 18px;
}

.brand-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  border-radius: 14px;
  background: linear-gradient(135deg, #245bff, #8a22f5);
}

.dashboard-header h1 {
  font-family: 'montserrat-bold';
  font-size: 40px;
  line-height: 1.15;
  margin-bottom: 12px;
  color: #0c0d10;
}

.dashboard-header p {
  font-family: 'montserrat-semibold';
  font-size: 22px;
  color: #465266;
  margin-bottom: 0;
}

.credits-card,
.metric-card,
.chart-card,
.activity-card,
.agent-card {
  border-radius: 16px;
}

.credits-card {
  background: linear-gradient(110deg, #245bff 0%, #9822f5 100%);
  color: white;
}

.credits-card-top,
.credits-progress-header,
.metric-top,
.activity-title-line {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.credits-card h2 {
  font-family: 'montserrat-bold';
  font-size: 27px;
  margin-bottom: 12px;
}

.plan-line,
.credits-note {
  font-family: 'montserrat-bold';
  font-size: 18px;
}

.buy-credits-button {
  min-width: 196px;
  height: 48px !important;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.18) !important;
  color: white !important;
  font-family: 'montserrat-bold';
  font-size: 16px;
  text-transform: none;
}

.credits-progress-header {
  font-family: 'montserrat-bold';
  font-size: 20px;
  margin-bottom: 12px;
}

.credits-progress {
  margin-bottom: 12px;
}

.metric-card {
  min-height: 274px;
  background: white;
}

.metric-top p {
  font-family: 'montserrat-semibold';
  font-size: 18px;
  color: #465266;
  margin-bottom: 0;
}

.metric-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 54px;
  height: 54px;
  border-radius: 14px;
}

.metric-icon-orange {
  background: #ffe6c9;
}

.metric-icon-blue {
  background: #d8e8ff;
}

.metric-icon-purple {
  background: #eadcff;
}

.metric-icon-green {
  background: #d9fae4;
}

.metric-card strong {
  display: block;
  margin-top: 48px;
  margin-bottom: 46px;
  font-family: 'montserrat-bold';
  font-size: 34px;
}

.metric-change {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'montserrat-bold';
  font-size: 17px;
  color: #00a342;
}

.section-title {
  font-family: 'montserrat-bold';
  font-size: 25px;
  line-height: 1.2;
  color: #0c0d10;
}

.chart-card,
.activity-card {
  min-height: 580px;
  background: white;
}

.chart-wrapper {
  display: flex;
  height: 320px;
  margin-top: 42px;
}

.chart-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 40px;
  padding-bottom: 30px;
  font-family: 'montserrat-semibold';
  font-size: 15px;
  color: #6b7280;
  text-align: right;
}

.chart-area {
  position: relative;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 16px;
  flex: 1;
  padding: 0 10px 30px 12px;
  border-left: 1px solid #8e98a8;
  border-bottom: 1px solid #8e98a8;
}

.chart-grid-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  border-top: 1px dashed #dce1e8;
}

.chart-grid-line:nth-child(1) {
  top: 0;
}

.chart-grid-line:nth-child(2) {
  top: 25%;
}

.chart-grid-line:nth-child(3) {
  top: 50%;
}

.chart-grid-line:nth-child(4) {
  top: 75%;
}

.chart-grid-line:nth-child(5) {
  top: 100%;
}

.chart-day {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  min-width: 0;
}

.chart-day::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  border-left: 1px dashed #dce1e8;
}

.bars-group {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 9px;
  width: 100%;
  height: 100%;
}

.chart-bar {
  width: 14px;
  min-height: 0;
  border-radius: 7px 7px 0 0;
}

.bar-messages {
  background: #13bd86;
}

.bar-calls {
  background: #3b82f6;
}

.bar-analysis {
  background: #f59e0b;
}

.bar-campaigns {
  background: #8b5cf6;
}

.chart-day span {
  position: absolute;
  bottom: -28px;
  font-family: 'montserrat-semibold';
  font-size: 16px;
  color: #6b7280;
}

.chart-legend {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 60px;
  font-family: 'montserrat-semibold';
  font-size: 20px;
}

.chart-legend div {
  display: flex;
  align-items: center;
  gap: 6px;
}

.chart-legend span {
  width: 18px;
  height: 18px;
  border-radius: 50%;
}

.legend-messages {
  color: #13bd86;
}

.legend-messages span {
  background: #13bd86;
}

.legend-calls {
  color: #3b82f6;
}

.legend-calls span {
  background: #3b82f6;
}

.legend-analysis {
  color: #f59e0b;
}

.legend-analysis span {
  background: #f59e0b;
}

.legend-campaigns {
  color: #8b5cf6;
}

.legend-campaigns span {
  background: #8b5cf6;
}

.activity-row {
  display: flex;
  gap: 20px;
  padding: 16px 0;
}

.activity-row.with-border {
  border-bottom: 1px solid #edf0f3;
}

.activity-icon,
.agent-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 54px;
  height: 54px;
  border-radius: 12px;
}

.activity-icon-blue {
  background: #d8e8ff;
}

.activity-icon-pink {
  background: #ffe0f1;
}

.activity-icon-orange {
  background: #ffe6c9;
}

.activity-icon-green {
  background: #d9fae4;
}

.activity-content {
  flex: 1;
  min-width: 0;
}

.activity-title-line h3 {
  font-family: 'montserrat-bold';
  font-size: 18px;
  margin-bottom: 8px;
}

.activity-title-line .v-chip {
  font-family: 'montserrat-bold';
}

.activity-content p {
  font-family: 'montserrat-semibold';
  font-size: 18px;
  color: #465266;
  margin-bottom: 8px;
}

.activity-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'montserrat-semibold';
  font-size: 15px;
  color: #6b7280;
}

.activity-meta span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.activity-meta .v-chip {
  font-family: 'montserrat-bold';
}

.agent-card {
  min-height: 352px;
  background: white;
}

.agent-heading {
  display: flex;
  align-items: flex-start;
  gap: 22px;
  margin-bottom: 56px;
}

.agent-heading h3 {
  font-family: 'montserrat-bold';
  font-size: 25px;
  margin-bottom: 8px;
}

.agent-heading p {
  font-family: 'montserrat-semibold';
  font-size: 18px;
  color: #465266;
  margin-bottom: 0;
}

.agent-icon {
  min-width: 64px;
  height: 64px;
  border-radius: 16px;
}

.agent-icon-blue {
  background: #0c86d5;
}

.agent-icon-purple {
  background: #9822f5;
}

.agent-icon-pink {
  background: #e80072;
}

.agent-icon-orange {
  background: #ef6600;
}

.agent-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 56px;
}

.agent-stats span {
  display: block;
  margin-bottom: 8px;
  font-family: 'montserrat-semibold';
  font-size: 16px;
  color: #465266;
}

.agent-stats strong {
  font-family: 'montserrat-bold';
  font-size: 24px;
}

.configure-button {
  height: 48px !important;
  border-color: #d8d9df;
  border-radius: 8px;
  background: white;
  font-family: 'montserrat-bold';
  font-size: 16px;
  color: #111111;
  text-transform: none;
}

@media (max-width: 960px) {
  .agents-dashboard-page {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }

  .dashboard-header {
    align-items: flex-start;
  }

  .dashboard-header h1 {
    font-size: 32px;
  }

  .dashboard-header p {
    font-size: 18px;
  }

  .credits-card-top,
  .credits-progress-header,
  .activity-title-line {
    flex-direction: column;
    align-items: flex-start;
  }

  .buy-credits-button {
    width: 100%;
  }

  .chart-wrapper {
    overflow-x: auto;
  }

  .chart-area {
    min-width: 620px;
  }

  .activity-card,
  .chart-card,
  .agent-card {
    min-height: auto;
  }

  .agent-stats {
    grid-template-columns: 1fr;
    gap: 18px;
  }
}

/* Fresher AI shared visual language */
.agents-dashboard-page {
  min-height: 100vh;
  background: #f7f8fa;
  color: #0c0d10;
  font-family: 'montserrat-regular';
}

.agents-dashboard-content {
  width: 100%;
  max-width: 1740px;
  margin: 0 auto;
  padding: 28px 40px 32px;
}

.dashboard-header {
  gap: 14px;
  margin-bottom: 28px;
}

.brand-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: #eaf4fd;
}

.dashboard-header h1 {
  margin-bottom: 5px;
  font-size: 28px;
  line-height: 1.2;
}

.dashboard-header p {
  margin-bottom: 0;
  font-family: 'montserrat-regular';
  font-size: 14px;
  color: #718096;
}

.dashboard-alert,
.dashboard-loading,
.dashboard-section {
  margin-bottom: 22px;
}

.dashboard-loading .dashboard-grid {
  margin-top: 22px;
}

.dashboard-card {
  border: 1px solid #e2e7ed !important;
  border-radius: 12px !important;
  background: #ffffff !important;
  color: #0c0d10 !important;
  box-shadow: 0 3px 12px rgba(30, 47, 70, 0.04) !important;
}

.credits-card,
.risk-agent-card,
.activity-card {
  padding: 26px 28px;
}

.credits-card h2 {
  margin-bottom: 6px;
  font-size: 18px;
}

.plan-line {
  margin-bottom: 0 !important;
  font-family: 'montserrat-regular';
  font-size: 13px;
  color: #718096;
}

.buy-credits-button,
.primary-action {
  height: 42px !important;
  border-radius: 8px;
  background: #1876d2 !important;
  color: #ffffff !important;
  font-family: 'montserrat-bold';
  font-size: 13px;
  text-transform: none;
}

.buy-credits-button {
  min-width: 174px;
}

.credits-balance {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-top: 30px;
}

.credits-balance strong {
  font-family: 'montserrat-bold';
  font-size: 42px;
  line-height: 1;
}

.credits-balance span {
  font-size: 14px;
  color: #596579;
}

.credits-breakdown {
  display: flex;
  gap: 42px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #edf0f3;
}

.credits-breakdown span,
.credits-breakdown strong {
  display: block;
}

.credits-breakdown span {
  margin-bottom: 5px;
  font-size: 12px;
  color: #7a8699;
}

.credits-breakdown strong {
  font-family: 'montserrat-semibold';
  font-size: 16px;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
  gap: 22px;
}

.risk-agent-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 300px;
}

.activity-card {
  min-height: 300px;
  max-height: 420px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.activity-list {
  min-height: 0;
  padding-right: 6px;
  overflow-y: auto;
}

.risk-agent-heading {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.risk-agent-icon,
.activity-empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 46px;
  height: 46px;
  border-radius: 12px;
  background: #eaf4fd;
}

.eyebrow {
  display: block;
  margin-bottom: 8px;
  font-family: 'montserrat-bold';
  font-size: 10px;
  letter-spacing: 0.08em;
  color: #1876d2;
}

.risk-agent-card h2,
.activity-card > h2 {
  font-family: 'montserrat-bold';
  font-size: 18px;
  color: #111827;
}

.risk-agent-card h2 {
  margin-bottom: 8px;
}

.risk-agent-card p {
  max-width: 520px;
  margin-bottom: 0;
  font-size: 13px;
  line-height: 1.7;
  color: #718096;
}

.primary-action {
  align-self: flex-start;
  padding: 0 20px !important;
}

.activity-card > h2 {
  margin-bottom: 18px;
}

.activity-empty {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 194px;
  padding: 20px;
  border-radius: 10px;
  background: #f7f9fb;
}

.activity-empty-icon {
  background: #edf1f5;
}

.activity-empty h3 {
  margin-bottom: 4px;
  font-family: 'montserrat-semibold';
  font-size: 14px;
}

.activity-empty p {
  margin-bottom: 0;
  font-size: 12px;
  color: #7a8699;
}

.activity-row {
  gap: 14px;
  padding: 14px 0;
}

.activity-icon {
  min-width: 42px;
  height: 42px;
  border-radius: 10px;
}

.activity-title-line h3 {
  margin-bottom: 6px;
  font-size: 13px;
}

.activity-title-line .v-chip,
.activity-meta .v-chip {
  font-family: 'montserrat-semibold';
}

.activity-content p {
  margin-bottom: 6px;
  font-family: 'montserrat-regular';
  font-size: 12px;
  color: #718096;
}

.activity-meta {
  font-family: 'montserrat-regular';
  font-size: 11px;
  color: #7a8699;
}

@media (max-width: 960px) {
  .agents-dashboard-content {
    padding: 24px;
  }

  .dashboard-header h1 {
    font-size: 26px;
  }

  .dashboard-header p {
    font-size: 13px;
  }

  .dashboard-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .agents-dashboard-content {
    padding: 20px 16px 28px;
  }

  .dashboard-header {
    align-items: flex-start;
  }

  .credits-card,
  .risk-agent-card,
  .activity-card {
    padding: 22px 18px;
  }

  .credits-breakdown {
    flex-direction: column;
    gap: 16px;
  }

  .credits-balance strong {
    font-size: 36px;
  }
}
</style>
